import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Button,
  Badge,
  Spinner,
} from "reactstrap";
import { withTranslation, useTranslation } from "react-i18next";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import swal from "sweetalert";
import { addvoyageorganize, getAllPlaces } from "helpers/requests";
import Breadcrumbs from "../../components/Common/Breadcrumb";

// 58 wilayas
const ALGERIAN_WILAYAS = [
  "Adrar", "Chlef", "Laghouat", "Oum El Bouaghi", "Batna", "Bejaia", "Biskra",
  "Bechar", "Blida", "Bouira", "Tamanrasset", "Tebessa", "Tlemcen", "Tiaret",
  "Tizi Ouzou", "Alger", "Djelfa", "Jijel", "Setif", "Saida", "Skikda",
  "Sidi Bel Abbes", "Annaba", "Guelma", "Constantine", "Medea", "Mostaganem",
  "M'sila", "Mascara", "Ouargla", "Oran", "El Bayadh", "Illizi",
  "Bordj Bou Arreridj", "Boumerdes", "El Tarf", "Tindouf", "Tissemsilt",
  "El Oued", "Khenchela", "Souk Ahras", "Tipaza", "Mila", "Ain Defla",
  "Naama", "Ain Temouchent", "Ghardaia", "Relizane", "Timimoun",
  "Bordj Badji Mokhtar", "Ouled Djellal", "Beni Abbes", "In Salah",
  "In Guezzam", "Touggourt", "Djanet", "El M'Ghair", "El Menia",
];

const formatDate = (s) =>
  s ? new Date(s).toLocaleDateString("fr-FR") : "—";

const VoyageAdd = ({ t }) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [isInAlgeria, setIsInAlgeria] = useState(false);

  const [formData, setFormData] = useState({
    code: "",
    title: "",
    title_ar: "",
    cite: "",
    street: "",
    airline: "",
    description: "",
    description_ar: "",
    availability: true,
    departures: [],
    excursions: [],
    excursions_ar: [],
    options: [],
    images: [],
    property_id: "",
    document: null,
  });

  const [imagePreview, setImagePreview] = useState([]);
  const [selectedRange, setSelectedRange] = useState([]);
  const [excursionInput, setExcursionInput] = useState("");
  const [excursionInputAr, setExcursionInputAr] = useState("");
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const flatpickrRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await getAllPlaces();
        setPlaces((response || []).filter((p) => p.available === true));
      } catch (error) {
        console.error("Error fetching places:", error);
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ── Files ──────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, images: [...prev.images, ...files] }));
    const previews = files.map((f) => URL.createObjectURL(f));
    setImagePreview((prev) => [...prev, ...previews]);
    e.target.value = "";
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    URL.revokeObjectURL(imagePreview[index]);
    setImagePreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setFormData((prev) => ({ ...prev, document: file }));
  };

  // ── Departures ──────────────────────────────────────────────
  const handleDateChange = (dates) => {
    if (dates.length === 2) {
      const [start, end] = dates;
      setSelectedRange([
        start.toISOString().split("T")[0],
        end ? end.toISOString().split("T")[0] : null,
      ]);
    }
  };

  const addDeparture = () => {
    if (selectedRange.length === 2 && selectedRange[1]) {
      setFormData((prev) => ({
        ...prev,
        departures: [
          ...prev.departures,
          { start: selectedRange[0], end: selectedRange[1] },
        ],
      }));
      setSelectedRange([]);
      flatpickrRef.current?.flatpickr.clear();
    }
  };

  const removeDeparture = (idx) =>
    setFormData((prev) => ({
      ...prev,
      departures: prev.departures.filter((_, i) => i !== idx),
    }));

  // ── Excursions ──────────────────────────────────────────────
  const addExcursion = () => {
    const v = excursionInput.trim();
    if (!v) return;
    if (formData.excursions.includes(v)) return setExcursionInput("");
    setFormData((prev) => ({ ...prev, excursions: [...prev.excursions, v] }));
    setExcursionInput("");
  };

  const removeExcursion = (idx) =>
    setFormData((prev) => ({
      ...prev,
      excursions: prev.excursions.filter((_, i) => i !== idx),
    }));

  const addExcursionAr = () => {
    const v = excursionInputAr.trim();
    if (!v) return;
    if (formData.excursions_ar.includes(v)) return setExcursionInputAr("");
    setFormData((prev) => ({
      ...prev,
      excursions_ar: [...prev.excursions_ar, v],
    }));
    setExcursionInputAr("");
  };

  const removeExcursionAr = (idx) =>
    setFormData((prev) => ({
      ...prev,
      excursions_ar: prev.excursions_ar.filter((_, i) => i !== idx),
    }));

  // ── Options (places) ────────────────────────────────────────
  const addOption = () => {
    const placeObject = places.find((p) => p.code === selectedPlace);
    if (!placeObject) {
      swal("Erreur", "Veuillez sélectionner un emplacement valide.", "error");
      return;
    }
    if (formData.options.some((o) => o.id === placeObject.id)) {
      swal("Doublon", "Cet emplacement a déjà été ajouté.", "warning");
      return;
    }
    setFormData((prev) => ({ ...prev, options: [...prev.options, placeObject] }));
    setSelectedPlace("");
  };

  const removeOption = (id) =>
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((o) => o.id !== id),
    }));

  // ── Validation ──────────────────────────────────────────────
  const validateForm = () => {
    if (!formData.code.trim()) return swal("Erreur", "Le code est obligatoire.", "error"), false;
    if (!formData.title.trim()) return swal("Erreur", "Le titre est obligatoire.", "error"), false;
    if (!formData.title_ar.trim()) return swal("Erreur", "العنوان بالعربية مطلوب", "error"), false;
    if (!formData.cite.trim()) return swal("Erreur", "La ville est obligatoire.", "error"), false;
    if (!formData.street.trim()) return swal("Erreur", "L'adresse est obligatoire.", "error"), false;
    if (!formData.airline.trim()) return swal("Erreur", "La compagnie aérienne est obligatoire.", "error"), false;
    if (!formData.description.trim()) return swal("Erreur", "La description est obligatoire.", "error"), false;
    if (!formData.description_ar.trim()) return swal("Erreur", "الوصف بالعربية مطلوب", "error"), false;
    if (formData.departures.length === 0) return swal("Erreur", "Ajoutez au moins une période de départ.", "error"), false;
    if (formData.excursions.length === 0) return swal("Erreur", "Ajoutez au moins une excursion.", "error"), false;
    if (formData.excursions_ar.length === 0) return swal("Erreur", "أضف رحلة واحدة على الأقل بالعربية.", "error"), false;
    if (formData.options.length === 0) return swal("Erreur", "Ajoutez au moins un emplacement.", "error"), false;
    if (formData.images.length === 0) return swal("Erreur", "Téléversez au moins une image.", "error"), false;
    if (!formData.document) return swal("Erreur", "Téléversez le document du voyage.", "error"), false;
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      const processed = {
        ...formData,
        availability: Boolean(formData.availability),
        property_id: formData.options[0].property_id,
      };
      Object.keys(processed).forEach((key) => {
        if (key === "images" || key === "document") return;
        if (
          key === "departures" ||
          key === "excursions" ||
          key === "excursions_ar" ||
          key === "options"
        ) {
          fd.append(key, JSON.stringify(processed[key]));
        } else {
          fd.append(key, processed[key]);
        }
      });
      formData.images.forEach((img) => fd.append("images", img));
      if (formData.document) fd.append("document", formData.document);

      await addvoyageorganize(fd);
      await swal("Succès", "Le voyage a été créé avec succès.", "success");
      window.location.href = "/voyage-organise";
    } catch (err) {
      const dup =
        err?.response?.status === 400 &&
        err?.response?.data?.message === "Voyage with this code already exists";
      swal(
        "Erreur",
        dup
          ? "Un voyage avec ce code existe déjà. Veuillez en choisir un autre."
          : "Erreur lors de la création du voyage.",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <div className={`page-content ${isRTL ? "rtl" : ""}`}>
        <Container fluid>
          <Breadcrumbs
            title={t("Voyages organisés")}
            breadcrumbItem={t("Nouveau voyage")}
          />

          <Form onSubmit={handleSubmit}>
            {/* ─── 1. Voyage info ───────────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <div className="mb-4">
                      <h5 className="mb-1">
                        <i className="mdi mdi-airplane text-primary me-2" />
                        Informations du voyage
                      </h5>
                      <p className="text-muted mb-0">
                        Identité, destination et descriptions visibles côté client.
                      </p>
                    </div>

                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Code <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-pound" /></InputGroupText>
                            <Input
                              name="code"
                              value={formData.code}
                              onChange={handleChange}
                              placeholder="VOY-2026-01"
                            />
                          </InputGroup>
                          <small className="text-muted">Identifiant unique du voyage.</small>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Titre (Français) <span className="text-danger">*</span></Label>
                          <Input
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            placeholder="Découverte du Sahara algérien"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>العنوان (عربي) <span className="text-danger">*</span></Label>
                          <Input
                            name="title_ar"
                            value={formData.title_ar}
                            onChange={handleChange}
                            placeholder="رحلة منظمة إلى الصحراء"
                            dir="rtl"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Localisation</Label>
                          <Input
                            type="select"
                            value={isInAlgeria ? "algeria" : "other"}
                            onChange={(e) => {
                              setIsInAlgeria(e.target.value === "algeria");
                              setFormData((prev) => ({ ...prev, cite: "" }));
                            }}
                          >
                            <option value="algeria">En Algérie</option>
                            <option value="other">Hors Algérie</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Ville / Wilaya <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-map-marker" /></InputGroupText>
                            {isInAlgeria ? (
                              <Input
                                type="select"
                                name="cite"
                                value={formData.cite}
                                onChange={handleChange}
                              >
                                <option value="">Sélectionner une wilaya</option>
                                {ALGERIAN_WILAYAS.map((w) => (
                                  <option key={w} value={w}>{w}</option>
                                ))}
                              </Input>
                            ) : (
                              <Input
                                name="cite"
                                value={formData.cite}
                                onChange={handleChange}
                                placeholder="Istanbul, Paris, Dubaï…"
                              />
                            )}
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Adresse / Rue <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-road-variant" /></InputGroupText>
                            <Input
                              name="street"
                              value={formData.street}
                              onChange={handleChange}
                              placeholder="Bab el Oued"
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Compagnie aérienne <span className="text-danger">*</span></Label>
                          <InputGroup>
                            <InputGroupText><i className="mdi mdi-airplane-takeoff" /></InputGroupText>
                            <Input
                              name="airline"
                              value={formData.airline}
                              onChange={handleChange}
                              placeholder="Air Algérie"
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label className="d-block">Disponibilité</Label>
                          <div className="form-check form-switch form-switch-md">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="availability"
                              name="availability"
                              checked={formData.availability}
                              onChange={handleChange}
                            />
                            <label className="form-check-label ms-2" htmlFor="availability">
                              {formData.availability ? (
                                <Badge color="success">Disponible</Badge>
                              ) : (
                                <Badge color="secondary">Indisponible</Badge>
                              )}
                            </label>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Description (Français) <span className="text-danger">*</span></Label>
                          <Input
                            type="textarea"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows={5}
                            placeholder="Présentez le voyage en quelques phrases…"
                          />
                          <small className="text-muted">{formData.description.length} caractères</small>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>الوصف (عربي) <span className="text-danger">*</span></Label>
                          <Input
                            type="textarea"
                            name="description_ar"
                            value={formData.description_ar}
                            onChange={handleChange}
                            rows={5}
                            placeholder="قدم الرحلة في بضع جمل…"
                            dir="rtl"
                          />
                          <small className="text-muted">{formData.description_ar.length} caractères</small>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 2. Departures ────────────────────────────────── */}
            <Row>
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-calendar-range text-primary me-2" />
                        Périodes de départ
                      </h5>
                      <p className="text-muted mb-0">
                        Sélectionnez une plage de dates puis cliquez sur "Ajouter".
                      </p>
                    </div>

                    <FormGroup>
                      <Label>Plage de dates</Label>
                      <InputGroup>
                        <InputGroupText><i className="mdi mdi-calendar-month" /></InputGroupText>
                        <Flatpickr
                          value={selectedRange.length ? selectedRange : []}
                          onChange={handleDateChange}
                          className="form-control"
                          placeholder="Sélectionner une période"
                          options={{ mode: "range", dateFormat: "Y-m-d" }}
                          ref={flatpickrRef}
                        />
                        <Button color="primary" type="button" onClick={addDeparture}>
                          <i className="mdi mdi-plus" /> Ajouter
                        </Button>
                      </InputGroup>
                    </FormGroup>

                    {formData.departures.length === 0 ? (
                      <div className="text-center text-muted py-4 border border-light rounded-3">
                        <i className="mdi mdi-calendar-blank display-6 d-block mb-2" />
                        Aucune période ajoutée.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {formData.departures.map((dep, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center justify-content-between p-2 border rounded-3 bg-light"
                          >
                            <div>
                              <i className="mdi mdi-calendar-arrow-right text-primary me-2" />
                              <strong>{formatDate(dep.start)}</strong>
                              <span className="text-muted mx-2">→</span>
                              <strong>{formatDate(dep.end)}</strong>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-soft-danger"
                              onClick={() => removeDeparture(idx)}
                            >
                              <i className="mdi mdi-close" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      {formData.departures.length} période(s) ajoutée(s)
                    </small>
                  </CardBody>
                </Card>
              </Col>

              {/* ─── 3. Options (places) ──────────────────────────── */}
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-package-variant text-primary me-2" />
                        Formules / Emplacements
                      </h5>
                      <p className="text-muted mb-0">
                        Sélectionnez les places (créées au préalable) à proposer.
                      </p>
                    </div>

                    <FormGroup>
                      <Label>Place</Label>
                      <InputGroup>
                        <InputGroupText><i className="mdi mdi-bed-outline" /></InputGroupText>
                        <Input
                          type="select"
                          value={selectedPlace}
                          onChange={(e) => setSelectedPlace(e.target.value)}
                        >
                          <option value="">Choisir une place</option>
                          {places.length === 0 ? (
                            <option value="" disabled>Aucune place disponible</option>
                          ) : (
                            places.map((p) => (
                              <option key={p.id} value={p.code}>
                                {p.code} — {p.name || ""}
                              </option>
                            ))
                          )}
                        </Input>
                        <Button color="primary" type="button" onClick={addOption}>
                          <i className="mdi mdi-plus" /> Ajouter
                        </Button>
                      </InputGroup>
                    </FormGroup>

                    {formData.options.length === 0 ? (
                      <div className="text-center text-muted py-4 border border-light rounded-3">
                        <i className="mdi mdi-package-variant-closed display-6 d-block mb-2" />
                        Aucune formule sélectionnée.
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {formData.options.map((opt) => (
                          <div
                            key={opt.id}
                            className="d-flex align-items-center justify-content-between p-2 border rounded-3 bg-light"
                          >
                            <div>
                              <strong>{opt.code}</strong>
                              <small className="text-muted ms-2">
                                {opt.max_adults} adulte(s) · {Number(opt.price).toLocaleString("fr-DZ")} DA
                              </small>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-soft-danger"
                              onClick={() => removeOption(opt.id)}
                            >
                              <i className="mdi mdi-close" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      {formData.options.length} formule(s) sélectionnée(s)
                    </small>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 4. Excursions ────────────────────────────────── */}
            <Row>
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-walk text-primary me-2" />
                        Excursions (Français)
                      </h5>
                      <p className="text-muted mb-0">
                        Listez les activités proposées pendant le voyage.
                      </p>
                    </div>
                    <InputGroup className="mb-3">
                      <InputGroupText><i className="mdi mdi-plus" /></InputGroupText>
                      <Input
                        value={excursionInput}
                        onChange={(e) => setExcursionInput(e.target.value)}
                        placeholder="Ex. Visite de la Casbah"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addExcursion();
                          }
                        }}
                      />
                      <Button color="primary" type="button" onClick={addExcursion}>
                        Ajouter
                      </Button>
                    </InputGroup>

                    {formData.excursions.length === 0 ? (
                      <div className="text-center text-muted py-4 border border-light rounded-3">
                        <i className="mdi mdi-clipboard-text-outline display-6 d-block mb-2" />
                        Aucune excursion.
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {formData.excursions.map((exc, idx) => (
                          <Badge
                            key={idx}
                            color="light"
                            className="d-inline-flex align-items-center text-dark border"
                            style={{ padding: "8px 12px", fontSize: 13, fontWeight: 500 }}
                          >
                            <i className="mdi mdi-check-circle text-success me-1" />
                            {exc}
                            <button
                              type="button"
                              onClick={() => removeExcursion(idx)}
                              className="btn-close ms-2"
                              style={{ fontSize: 9 }}
                              aria-label="Supprimer"
                            />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg={6}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-walk text-primary me-2" />
                        الرحلات (عربي)
                      </h5>
                      <p className="text-muted mb-0">
                        أضف الأنشطة المقترحة باللغة العربية.
                      </p>
                    </div>
                    <InputGroup className="mb-3">
                      <InputGroupText><i className="mdi mdi-plus" /></InputGroupText>
                      <Input
                        value={excursionInputAr}
                        onChange={(e) => setExcursionInputAr(e.target.value)}
                        placeholder="مثال: زيارة القصبة"
                        dir="rtl"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addExcursionAr();
                          }
                        }}
                      />
                      <Button color="primary" type="button" onClick={addExcursionAr}>
                        Ajouter
                      </Button>
                    </InputGroup>

                    {formData.excursions_ar.length === 0 ? (
                      <div className="text-center text-muted py-4 border border-light rounded-3">
                        <i className="mdi mdi-clipboard-text-outline display-6 d-block mb-2" />
                        لا توجد رحلات.
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2">
                        {formData.excursions_ar.map((exc, idx) => (
                          <Badge
                            key={idx}
                            color="light"
                            className="d-inline-flex align-items-center text-dark border"
                            style={{ padding: "8px 12px", fontSize: 13, fontWeight: 500, direction: "rtl" }}
                          >
                            <i className="mdi mdi-check-circle text-success me-1" />
                            {exc}
                            <button
                              type="button"
                              onClick={() => removeExcursionAr(idx)}
                              className="btn-close ms-2"
                              style={{ fontSize: 9 }}
                              aria-label="Supprimer"
                            />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 5. Files ─────────────────────────────────────── */}
            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-image-multiple-outline text-primary me-2" />
                        Photos
                      </h5>
                      <p className="text-muted mb-0">
                        Plusieurs images de qualité (paysages, activités, hôtel…).
                      </p>
                    </div>

                    <label
                      htmlFor="images"
                      className="d-block text-center border border-2 border-dashed rounded-3 p-4 mb-3"
                      style={{ cursor: "pointer", borderColor: "#cfd8e3" }}
                    >
                      <i className="mdi mdi-cloud-upload-outline display-6 text-primary mb-2" />
                      <p className="mb-1 fw-medium">Cliquez pour téléverser</p>
                      <small className="text-muted">JPG, PNG — plusieurs fichiers acceptés</small>
                      <input
                        type="file"
                        id="images"
                        name="images"
                        onChange={handleImageChange}
                        accept="image/*"
                        multiple
                        hidden
                      />
                    </label>

                    {imagePreview.length > 0 && (
                      <div className="d-flex flex-wrap gap-2">
                        {imagePreview.map((src, idx) => (
                          <div
                            key={idx}
                            style={{
                              width: 110,
                              height: 110,
                              position: "relative",
                              borderRadius: 8,
                              overflow: "hidden",
                              border: "1px solid #e5e9f2",
                            }}
                          >
                            <img
                              src={src}
                              alt={`Aperçu ${idx + 1}`}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="btn btn-danger btn-sm"
                              style={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                width: 24,
                                height: 24,
                                padding: 0,
                                lineHeight: 1,
                                borderRadius: "50%",
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="text-muted d-block mt-2">
                      {imagePreview.length} image(s) ajoutée(s)
                    </small>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={4}>
                <Card>
                  <CardBody>
                    <div className="mb-3">
                      <h5 className="mb-1">
                        <i className="mdi mdi-file-pdf-box text-primary me-2" />
                        Document du voyage
                      </h5>
                      <p className="text-muted mb-0">
                        PDF officiel : programme, conditions, etc.
                      </p>
                    </div>
                    <label
                      htmlFor="document"
                      className="d-block text-center border border-2 border-dashed rounded-3 p-4"
                      style={{ cursor: "pointer", borderColor: "#cfd8e3" }}
                    >
                      {formData.document ? (
                        <>
                          <i className="mdi mdi-file-check-outline display-6 text-success mb-2" />
                          <p className="mb-1 fw-medium">{formData.document.name}</p>
                          <small className="text-muted">
                            {(formData.document.size / 1024).toFixed(1)} Ko · cliquez pour remplacer
                          </small>
                        </>
                      ) : (
                        <>
                          <i className="mdi mdi-file-upload-outline display-6 text-primary mb-2" />
                          <p className="mb-1 fw-medium">Choisir un PDF</p>
                          <small className="text-muted">PDF uniquement</small>
                        </>
                      )}
                      <input
                        type="file"
                        id="document"
                        name="document"
                        onChange={handleDocumentChange}
                        accept="application/pdf"
                        hidden
                      />
                    </label>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* ─── 6. Submit bar ────────────────────────────────── */}
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <Row className="align-items-center">
                      <Col md={6}>
                        <small className="text-muted">
                          Vérifiez toutes les sections avant de soumettre.
                        </small>
                      </Col>
                      <Col md={6} className="text-md-end mt-3 mt-md-0">
                        <Button
                          color="light"
                          type="button"
                          onClick={() => (window.location.href = "/voyage-organise")}
                          disabled={submitting}
                        >
                          Annuler
                        </Button>
                        <Button
                          color="primary"
                          type="submit"
                          disabled={submitting}
                          className="ms-2"
                        >
                          {submitting ? (
                            <>
                              <Spinner size="sm" className="me-2" /> Enregistrement…
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-check-bold me-1" />
                              Créer le voyage
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(VoyageAdd);
